/**
 * Symbol Registry Store
 *
 * Manages dynamic symbol definitions fetched from the backend.
 * Provides a single source of truth for symbol specifications.
 */

import { create } from 'zustand';
import { getBackendUrl } from '../config/ports';

export interface SymbolDefinition {
  base: string;
  fullSymbol: string;
  label: string;
  tickSize: number;
  tickValue: number;
  pointValue: number;
  exchange: string;
  enabled: boolean;
  /** 'perp' = a non-crypto Vest chart feed market (chart only). */
  instrumentType?: 'futures' | 'crypto' | 'perp';
  /** Vest chart feed: candle volume is qty * 10^sizeDecimals (divided back for display). */
  sizeDecimals?: number;
}

interface SymbolStoreState {
  // Symbol registry from backend
  symbols: SymbolDefinition[];

  /**
   * The contracts the engine is actually streaming right now, newest roll
   * included (`/api/symbols` -> `symbols`, refreshed by `server-status`).
   *
   * Kept separate from `symbols` on purpose. The registry is user-editable
   * configuration persisted in Redis, so its `fullSymbol` goes stale the
   * moment a contract rolls and nobody edits it -- in September 2026 it still
   * said `NQM6.CME`, a contract whose last tick was in June. THIS list is the
   * engine's own answer and is the only safe input to `resolveActiveSymbol`.
   */
  activeSymbols: string[];

  // Loading state
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;

  // Actions
  fetchSymbols: () => Promise<void>;
  setActiveSymbols: (symbols: string[]) => void;
  addSymbol: (symbol: SymbolDefinition) => Promise<boolean>;
  updateSymbol: (symbol: SymbolDefinition) => Promise<boolean>;
  removeSymbol: (base: string) => Promise<boolean>;

  // Getters
  getSymbol: (baseOrFull: string) => SymbolDefinition | null;
  getAvailableSymbols: () => { value: string; label: string }[];
  normalizeSymbol: (symbol: string) => string | null;
}

// Default symbol specs as fallback (hardcoded)
export const DEFAULT_SYMBOLS: SymbolDefinition[] = [
  { base: 'MNQ', fullSymbol: 'MNQU6.CME', label: 'MNQ (Micro Nasdaq)', tickSize: 0.25, tickValue: 0.5, pointValue: 2.0, exchange: 'CME', enabled: true, instrumentType: 'futures' },
  { base: 'NQ', fullSymbol: 'NQU6.CME', label: 'NQ (E-mini Nasdaq)', tickSize: 0.25, tickValue: 5.0, pointValue: 20.0, exchange: 'CME', enabled: true, instrumentType: 'futures' },
  { base: 'MES', fullSymbol: 'MESU6.CME', label: 'MES (Micro S&P 500)', tickSize: 0.25, tickValue: 1.25, pointValue: 5.0, exchange: 'CME', enabled: true, instrumentType: 'futures' },
  { base: 'ES', fullSymbol: 'ESU6.CME', label: 'ES (E-mini S&P 500)', tickSize: 0.25, tickValue: 12.5, pointValue: 50.0, exchange: 'CME', enabled: true, instrumentType: 'futures' },
  { base: 'SIL', fullSymbol: 'SILH6.COMEX', label: 'SIL (Micro Silver)', tickSize: 0.005, tickValue: 5.0, pointValue: 1000.0, exchange: 'COMEX', enabled: true, instrumentType: 'futures' },
  { base: 'ETHUSD-BITFINEX', fullSymbol: 'ETHUSD-BITFINEX', label: 'ETH/USD (Ethereum)', tickSize: 0.01, tickValue: 0.01, pointValue: 1.0, exchange: 'BITFINEX', enabled: true, instrumentType: 'crypto' },
  { base: 'SOLUSDT-BMEX', fullSymbol: 'SOLUSDT-BMEX', label: 'SOL/USDT (Solana)', tickSize: 0.001, tickValue: 0.001, pointValue: 1.0, exchange: 'BMEX', enabled: true, instrumentType: 'crypto' },
  // Vest chart feeds (#622 BTC, #626 ETH/SOL): Vest's tape, chart only. The
  // engine adds the same entries to /api/symbols/registry with the tick size
  // from each file's header. BTC tick 0.1 since 2026-10-14 (#626). Ticks + sizeDecimals are Vest's
  // exchangeInfo (minTickSize / sizeDecimals, recorded 2026-10-03..10-31).
  { base: 'BTC-PERP.VEST', fullSymbol: 'BTC-PERP.VEST', label: 'BTC', tickSize: 0.1, tickValue: 0.1, pointValue: 1.0, exchange: 'VEST', enabled: true, instrumentType: 'crypto', sizeDecimals: 4 },
  { base: 'ETH-PERP.VEST', fullSymbol: 'ETH-PERP.VEST', label: 'ETH', tickSize: 0.01, tickValue: 0.01, pointValue: 1.0, exchange: 'VEST', enabled: true, instrumentType: 'crypto', sizeDecimals: 3 },
  { base: 'SOL-PERP.VEST', fullSymbol: 'SOL-PERP.VEST', label: 'SOL', tickSize: 0.001, tickValue: 0.001, pointValue: 1.0, exchange: 'VEST', enabled: true, instrumentType: 'crypto', sizeDecimals: 2 },
];

export const useSymbolStore = create<SymbolStoreState>((set, get) => ({
  symbols: DEFAULT_SYMBOLS, // Start with defaults
  activeSymbols: [],
  isLoading: false,
  error: null,
  lastFetched: null,

  setActiveSymbols: (symbols: string[]) => {
    const next = (symbols || []).filter(Boolean);
    const prev = get().activeSymbols;
    // Reference-stable when nothing rolled, so subscribers do not re-run on
    // every `server-status` heartbeat.
    if (prev.length === next.length && prev.every((s, i) => s === next[i])) return;
    set({ activeSymbols: next });
  },

  fetchSymbols: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await fetch(`${getBackendUrl()}/api/symbols/registry`);
      const data = await response.json();

      if (data.success && data.symbols?.length > 0) {
        set({
          symbols: data.symbols,
          isLoading: false,
          lastFetched: Date.now()
        });
      } else if (data.symbols?.length === 0) {
        // No symbols in registry, keep defaults
        console.log('Symbol registry empty, using defaults');
        set({ isLoading: false, lastFetched: Date.now() });
      } else {
        set({
          error: data.error || 'Failed to fetch symbols',
          isLoading: false
        });
      }
    } catch (error) {
      console.error('Failed to fetch symbol registry:', error);
      set({
        error: error instanceof Error ? error.message : 'Network error',
        isLoading: false
      });
    }
  },

  addSymbol: async (symbol: SymbolDefinition) => {
    try {
      const response = await fetch(`${getBackendUrl()}/api/symbols/registry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(symbol),
      });
      const data = await response.json();

      if (data.success) {
        // Refetch to get updated list
        await get().fetchSymbols();
        return true;
      } else {
        console.error('Failed to add symbol:', data.error);
        return false;
      }
    } catch (error) {
      console.error('Failed to add symbol:', error);
      return false;
    }
  },

  updateSymbol: async (symbol: SymbolDefinition) => {
    // Same as add (upsert)
    return get().addSymbol(symbol);
  },

  removeSymbol: async (base: string) => {
    try {
      const response = await fetch(`${getBackendUrl()}/api/symbols/registry/${base}`, {
        method: 'DELETE',
      });
      const data = await response.json();

      if (data.success) {
        // Refetch to get updated list
        await get().fetchSymbols();
        return true;
      } else {
        console.error('Failed to remove symbol:', data.error);
        return false;
      }
    } catch (error) {
      console.error('Failed to remove symbol:', error);
      return false;
    }
  },

  getSymbol: (baseOrFull: string) => {
    const { symbols } = get();
    const upper = baseOrFull.toUpperCase();

    // Try exact match on base
    const byBase = symbols.find(s => s.base.toUpperCase() === upper);
    if (byBase) return byBase;

    // Try exact match on fullSymbol
    const byFull = symbols.find(s => s.fullSymbol.toUpperCase() === upper);
    if (byFull) return byFull;

    // Try prefix match (e.g., "MNQH6.CME" matches base "MNQ")
    // Sort by base length descending to match longest first (MNQ before NQ)
    const sortedSymbols = [...symbols].sort((a, b) => b.base.length - a.base.length);
    for (const sym of sortedSymbols) {
      if (upper.startsWith(sym.base.toUpperCase())) {
        return sym;
      }
    }

    return null;
  },

  getAvailableSymbols: () => {
    const { symbols } = get();
    return symbols
      .filter(s => s.enabled)
      .map(s => ({ value: s.fullSymbol, label: s.label }));
  },

  normalizeSymbol: (symbol: string) => {
    const sym = get().getSymbol(symbol);
    return sym ? sym.base : null;
  },
}));

// Initialize: fetch symbols on module load
useSymbolStore.getState().fetchSymbols();
